import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import MarkerEditor from './MarkerEditor';
import { text, markerId, isEventMarker } from '../markerUtils';
import './MarkerDetailsModal.css';

// The editing dialog for one marker. Closing with unsaved changes asks first.

export default function MarkerDetailsModal({ marker, onClose, onSaved, onDelete, onUnapprove, busy = false }) {
  const [dirty, setDirty] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const event = isEventMarker(marker);
  const title = text(marker?.title) || 'Ohne Titel';
  const id = markerId(marker);

  const requestClose = useCallback(() => {
    if (dirty) setAskDiscard(true);
    else onClose?.();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => { if (!dirty) setAskDiscard(false); }, [dirty]);

  if (!marker) return null;

  return createPortal(
    <div className="mdm-overlay" role="dialog" aria-modal="true" aria-labelledby="mdm-title">
      <button className="mdm-backdrop" onClick={requestClose} aria-label="Schließen" tabIndex={-1} />

      <div className={`mdm-card mdm-card--${event ? 'event' : 'club'}`}>
        <header className="mdm-head">
          <div className="mdm-headline">
            <span className={`badge ${event ? 'badge-event' : 'badge-club'}`}>{event ? 'Event' : 'Club'}</span>
            <h2 className="mdm-title" id="mdm-title">{title}</h2>
            <span className="mdm-id">#{id}</span>
            {dirty && <span className="mdm-dirty">Ungespeichert</span>}
          </div>
          <div className="mdm-actions">
            {onUnapprove && (
              <button type="button" className="btn btn-ghost mdm-btn" onClick={() => onUnapprove(marker)} disabled={busy}
                title="Nimmt den Marker aus der App und legt ihn unter Pending Events ab">
                Zurück in die Prüfung
              </button>
            )}
            {onDelete && (
              <button type="button" className="btn btn-ghost mdm-btn mdm-btn--danger" onClick={() => onDelete(marker)} disabled={busy}>
                Löschen
              </button>
            )}
            <button type="button" className="mdm-x" onClick={requestClose} aria-label="Dialog schließen">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="14" height="14" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        {askDiscard && (
          <div className="mdm-discard" role="alert">
            <span>Es gibt ungespeicherte Änderungen. Verwerfen?</span>
            <div className="mdm-discard-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setAskDiscard(false)}>Weiter bearbeiten</button>
              <button type="button" className="btn mdm-btn--danger" onClick={() => onClose?.()}>Verwerfen</button>
            </div>
          </div>
        )}

        <div className="mdm-body">
          <MarkerEditor marker={marker} onSaved={onSaved} onCancel={requestClose} onDirtyChange={setDirty} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
